﻿/**
 * 收件箱通知 HTTP Service — 对接后端通知 API
 */
import type {
  MessageNotification,
  MessageType,
  MessagePreference,
  NotificationListResponse,
  UnreadCountResponse,
  ExpiringSoonResponse,
} from '@/types/notifications'
import { httpClient, httpGet, httpPost, httpPut } from '@/utils/httpClient'
import { wrapResponse, type ApiResponse } from '@/services/types'

/**
 * 后端通知类型 → 前端 MessageType 映射表
 *
 * 后端 InAppNotification.type 为自由字符串（如 announcement_update、feedback_reply），
 * 前端 MessageType 为固定 5 种分类。此映射确保后端任何类型值都能正确归入前端分类，
 * 未知类型默认归入 'system'。
 */
const BACKEND_TYPE_TO_FRONTEND: Record<string, MessageType> = {
  // 公告相关 → 系统通知
  announcement_publish: 'system',
  announcement_update: 'system',
  announcement_recall: 'system',
  announcement: 'system',
  // 反馈相关 → 系统通知
  feedback_submit: 'system',
  feedback_reply: 'system',
  feedback_resolved: 'system',
  feedback_assigned: 'system',
  task_assignment: 'system',
  // 模板相关 → 系统通知
  template_created: 'system',
  template_updated: 'system',
  // 系统消息 → 系统通知
  system: 'system',
  system_admin: 'system',
  // 协作相关
  collaboration: 'collaboration',
  collaboration_invite: 'collaboration',
  // 待办相关
  todo: 'todo',
  todo_reminder: 'todo',
  deadline_warning: 'todo',
  task_urge: 'todo',
  // 审核相关
  audit: 'audit',
  audit_passed: 'audit',
  audit_rejected: 'audit',
  report_approval_pending: 'audit',
  report_approval_approved: 'audit',
  report_approval_rejected: 'audit',
  // AI 生成相关
  ai_generation: 'ai_generation',
  ai_report_ready: 'ai_generation',
  ai_analysis_complete: 'ai_generation',
}

/** 后端 InAppNotification 实体形态（camelCase JSON），入站映射的类型约束 */
interface BackendNotification {
  id: string
  title: string
  summary: string
  /** 公告类通知承载全文（HTML），其余场景为 null */
  content?: string | null
  type: string
  receiverId: string
  isRead: boolean
  readAt?: string | null
  isStarred?: boolean
  targetType?: string | null
  targetId?: string | null
  targetUrl?: string | null
  sentAt: string
  expiresAt: string
}

/** 后端 NotificationPreference 形态（camelCase JSON），入站映射的类型约束 */
interface BackendPreference {
  userId: string
  notificationType: string
  inboxEnabled: boolean
  badgeEnabled: boolean
  /** 邮件渠道开关（后端 EmailNotificationSender 消费） */
  emailEnabled?: boolean
}

/** 将后端 type 字符串映射为前端 MessageType，未知类型回退到 'system' */
function mapBackendType(type: string): MessageType {
  return BACKEND_TYPE_TO_FRONTEND[type] || 'system'
}

/**
 * 将后端 InAppNotification 实体转换为前端 MessageNotification
 */
function mapNotification(backend: BackendNotification): MessageNotification {
  return {
    id: backend.id,
    title: backend.title,
    // content 恒为纯文本摘要：列表 v-html 高亮渲染若混入 HTML 标签会被转义显示为字面源码，
    // 公告富文本全文走 fullContent 仅在详情抽屉渲染（评审跟进①）
    content: backend.summary,
    fullContent: backend.content || null,
    // 将后端自由字符串 type 映射为前端固定 MessageType，确保 UI 层不会因未知类型崩溃
    type: mapBackendType(backend.type),
    sender: { type: 'system', name: '系统' }, // 后端无 sender 字段，使用默认值
    receiverId: backend.receiverId,
    isRead: backend.isRead,
    readAt: backend.readAt ? new Date(backend.readAt) : null,
    isStarred: !!backend.isStarred,
    relatedBizType: (backend.targetType as MessageNotification['relatedBizType']) || null,
    relatedBizId: backend.targetId || null,
    // 后端写入的路由路径（如 /ledgers/{id}、/reports/{id}），跳转优先级高于 relatedBizType 映射
    targetUrl: backend.targetUrl || null,
    createdAt: new Date(backend.sentAt),
    expiresAt: new Date(backend.expiresAt),
  }
}

/**
 * 将后端 NotificationPreference 转换为前端 MessagePreference。
 * 前端展示 receive/badge/email 三维；sms/push 无真实通道不展示（字段仍透传存储）。
 */
function mapPreference(backend: BackendPreference): MessagePreference {
  return {
    userId: String(backend.userId),
    type: backend.notificationType as MessageType,
    receive: backend.inboxEnabled,
    badge: backend.badgeEnabled,
    email: backend.emailEnabled ?? false,
  }
}

export const httpInboxNotificationsService = {

  // ===== Notifications =====

  async fetchNotifications(params: {
    page?: number
    pageSize?: number
    filter?: string
    type?: string
    keyword?: string
    signal?: AbortSignal
  }): Promise<ApiResponse<NotificationListResponse>> {
    const { signal, ...queryParams } = params
    const d = await httpGet<any>('/api/system-messages/notifications', { params: queryParams, signal })
    const items = (d.items || []).map(mapNotification)
    return wrapResponse<NotificationListResponse>({
      list: items,
      total: d.total,
      // 后端分页响应不含 unreadTotal（全局未读由 unread-count 接口维护），不伪造本页统计
      hasMore: d.page * d.pageSize < d.total,
      expiringSoonCount: d.expiringSoonCount,
    })
  },

  async markAsRead(id: string): Promise<ApiResponse<null>> {
    await httpClient.post(`/api/system-messages/notifications/${id}/read`)
    return wrapResponse(null)
  },

  async markAllAsRead(): Promise<ApiResponse<{ marked: number }>> {
    const data = await httpPost<{ marked: number }>('/api/system-messages/notifications/read-all')
    return wrapResponse(data)
  },

  async batchMarkRead(ids: string[]): Promise<ApiResponse<{ marked: number }>> {
    const data = await httpPost<{ marked: number }>('/api/system-messages/notifications/batch-read', { ids })
    return wrapResponse(data)
  },

  async deleteNotification(id: string): Promise<ApiResponse<null>> {
    await httpClient.delete(`/api/system-messages/notifications/${id}`)
    return wrapResponse(null)
  },

  async batchDelete(ids: string[]): Promise<ApiResponse<{ deleted: number; failedIds: string[] }>> {
    const data = await httpPost<{ deleted: number; failedIds: string[] }>('/api/system-messages/notifications/batch-delete', { ids })
    return wrapResponse(data)
  },

  /** 标星/取消标星（后端 toggleStar，已读角标不受影响） */
  async toggleStar(id: string, starred: boolean): Promise<ApiResponse<null>> {
    await httpPost(`/api/system-messages/notifications/${id}/star`, { starred })
    return wrapResponse(null)
  },

  /** 公告阅读打卡：管理端阅读统计与发送日志阅读率的数据来源（fire-and-forget 调用） */
  async recordAnnouncementRead(announcementId: string): Promise<ApiResponse<null>> {
    await httpPost(`/api/system-messages/announcements/${announcementId}/read`)
    return wrapResponse(null)
  },

  async fetchUnreadCount(): Promise<ApiResponse<UnreadCountResponse>> {
    const data = await httpGet<{ count: number }>('/api/system-messages/notifications/unread-count')
    return wrapResponse({
      total: data.count,
    })
  },

  async fetchExpiringSoon(): Promise<ApiResponse<ExpiringSoonResponse>> {
    const data = await httpGet<{ count: number }>('/api/system-messages/notifications/expiring-soon')
    return wrapResponse({ count: data.count, daysUntilExpiry: 7 })
  },

  // ===== Preferences =====

  async fetchPreferences(): Promise<ApiResponse<{ preferences: MessagePreference[] }>> {
    const data = await httpGet<any[]>('/api/user/notification-preferences')
    return wrapResponse({ preferences: data.map(mapPreference) })
  },

  async updatePreference(type: MessageType, patch: Partial<MessagePreference>): Promise<ApiResponse<MessagePreference>> {
    const body: Record<string, any> = {}
    if (patch.receive !== undefined) body.inboxEnabled = patch.receive
    if (patch.badge !== undefined) body.badgeEnabled = patch.badge
    if (patch.email !== undefined) body.emailEnabled = patch.email
    const data = await httpPut<any>(`/api/user/notification-preferences/${type}`, body)
    return wrapResponse(mapPreference(data))
  },

  async batchUpdatePreferences(prefs: Array<{ type: MessageType } & Partial<MessagePreference>>): Promise<ApiResponse<null>> {
    const preferences = prefs.map(p => {
      const item: Record<string, any> = { notificationType: p.type }
      if (p.receive !== undefined) item.inboxEnabled = p.receive
      if (p.badge !== undefined) item.badgeEnabled = p.badge
      if (p.email !== undefined) item.emailEnabled = p.email
      return item
    })
    await httpClient.put('/api/user/notification-preferences/batch', { preferences })
    return wrapResponse(null)
  },
}
